import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Autocomplete,
  Avatar,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../../Redux/Reducers/uiSlice";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import {
  findStrategy,
  findStrategyById,
  investArr,
  hashInvestment,
  hashInvestmentReverse,
} from "../../../component/Methods";
import { sendWebSocketMessage } from "../../../hooks/useWebSocket";
import { useTradeBlotterState } from "../../../hooks/useOptimizedState";

const EditTradeDialog = ({ open, onClose, tradeData }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  // Redux selectors
  const { funds, accountingData, strategies, brokers, lots } =
    useTradeBlotterState();

  const accFunds = accountingData[0]?.funds;
  const user = useSelector((state) => state.auth?.user?.email);

  // Form state
  const [formData, setFormData] = useState({
    fund: "",
    strategy: "",
    action: "",
    ticker: "",
    option: 1, // Options enum: calls=0, puts=1 (default puts)
    strikePrice: "",
    expirationDate: "", // YYYY-MM-DD
    broker: "",
    account: "",
    shareCount: "",
    routing: "",
    assetType: "Equity",
  });

  // Memoized sorted options
  const sortedFunds = useMemo(() => {
    return [...(funds || [])].sort((a, b) =>
      (a.abbreviation || "").localeCompare(b.abbreviation || "")
    );
  }, [funds]);

  const sortedBrokers = useMemo(() => {
    const sorted = [...(brokers || [])].sort((a, b) =>
      (a.abbreviation || "").localeCompare(b.abbreviation || "")
    );

    return sorted;
  }, [brokers]);

  // Initialize form with trade data when dialog opens
  useEffect(() => {
    if (tradeData && open) {
      // Get asset type from trade data's investment type
      const assetTypeName =
        tradeData.investment !== undefined
          ? hashInvestment(tradeData.investment) || "Equity"
          : "Equity";

      const actionFromActionPosition = (action, position) => {
        if (action === "Sell" && position === "Short") return "Short";
        if (action === "Buy" && position === "Short") return "Cover";
        if (action === "Sell" && position === "Long") return "Sell";
        if (action === "Buy" && position === "Long") return "Buy";
        return action || "";
      };
      const initialAction = actionFromActionPosition(
        tradeData?.action,
        tradeData?.position
      );

      const existingOption = tradeData?.lastOrder?.ticker?.option;
      const existingStrikePrice = tradeData?.lastOrder?.ticker?.strikePrice;
      const existingExpirationDate = tradeData?.lastOrder?.ticker?.expirationDate;
      const expirationDateYmd = existingExpirationDate
        ? new Date(existingExpirationDate).toISOString().slice(0, 10)
        : "";

      setFormData({
        fund: tradeData.fundAbbreviation || "",
        strategy: tradeData.strategy || "",
        action: initialAction,
        ticker: tradeData.ticker || "",
        option: existingOption === 0 ? 0 : 1,
        strikePrice:
          existingStrikePrice !== undefined && existingStrikePrice !== null
            ? String(existingStrikePrice)
            : "",
        expirationDate: expirationDateYmd,
        broker: tradeData.brokerAbbreviation || "",
        account: tradeData.account || "",
        shareCount: tradeData.quantity || "",
        routing: tradeData.routing !== undefined ? tradeData.routing : "Manual",
        assetType: assetTypeName,
      });
    }
  }, [tradeData, open, sortedBrokers]);

  const sortedStrategies = useMemo(() => {
    if (!formData.fund) return [];
    const selectedFund = funds?.find(
      (fund) => fund.abbreviation === formData.fund
    );
    let fundStrategies = [];
    if (accFunds && accFunds.length > 0 && selectedFund?.fundId) {
      const selectedAccFund = accFunds?.find(
        (fund) => fund?.fund === selectedFund?.fundId
      );
      if (
        selectedAccFund?.strategyAUMs &&
        Object.keys(selectedAccFund.strategyAUMs).length > 0
      ) {
        // Get strategy names from the strategy IDs in strategyAUMs
        const strategyIds = Object.keys(selectedAccFund.strategyAUMs);

        fundStrategies = strategyIds.map((strategyId) => ({
          strategyId: strategyId,
          name: findStrategy(strategyId, strategies) || strategyId, // Use strategyId as fallback if name not found
        }));

        // Sort strategies alphabetically by name
        fundStrategies.sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
      }
    }

    if (fundStrategies.length === 0 && strategies?.length > 0) {
      fundStrategies = [];
    }

    return fundStrategies;
  }, [formData.fund, funds, strategies, accFunds]);
  const sortedAccounts = useMemo(() => {
    if (!formData.fund) return [];
    const selectedFund = funds?.find(
      (fund) => fund.abbreviation === formData.fund
    );
    if (!selectedFund?.accounts) return [];

    return Object.keys(selectedFund.accounts).sort();
  }, [formData.fund, funds]);

  // Get unique tickers from lots data (memoized)
  const uniqueTickers = useMemo(() => {
    if (!lots || lots.length === 0) return [];

    const tickerSet = new Set();
    const tickerObjects = [];

    lots.forEach((lot) => {
      if (
        lot?.ticker?.symbol &&
        !tickerSet.has(lot.ticker.symbol.toLowerCase())
      ) {
        // Only include tickers that start with alphabets (A-Z, a-z)
        const symbol = lot.ticker.symbol;
        if (/^[A-Za-z]/.test(symbol)) {
          tickerSet.add(symbol.toLowerCase());
          tickerObjects.push({
            symbol: symbol.toUpperCase(),
            investment: lot.ticker.investment,
            currency: lot.ticker.currency,
            exchange: lot.ticker.exchange,
          });
        }
      }
    });

    return tickerObjects.sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [lots]);

  const investmentTypeForUi = hashInvestmentReverse(
    formData.assetType.toLowerCase()
  );
  const showOptionFields = investmentTypeForUi === 10;

  const actionOptions = showOptionFields
    ? [
        { value: "Buy", label: "Buy to Open" },
        { value: "Cover", label: "Buy to Close" },
        { value: "Sell", label: "Sell to Close" },
        { value: "Short", label: "Sell to Open" },
      ]
    : [
      { value: "Buy", label: "Buy" },
      { value: "Cover", label: "Cover" },
      { value: "Sell", label: "Sell" },
      { value: "Short", label: "Short" },
    ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      if (field === "assetType") {
        const nextInvestmentType = hashInvestmentReverse(
          String(value || "Equity").toLowerCase()
        );
        return {
          ...prev,
          [field]: value,
          ...(nextInvestmentType === 10
            ? { option: prev.option ?? 1 }
            : { option: 1, strikePrice: "", expirationDate: "" }),
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });

    // Reset strategy when fund changes
    if (field === "fund") {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        strategy: "",
        account: "",
      }));
    }
  };

  const handleSubmit = () => {
    // Validate required fields
    if (
      !formData.fund ||
      !formData.action ||
      !formData.ticker ||
      !formData.broker ||
      !formData.account ||
      !formData.shareCount ||
      !formData.routing
    ) {
      alert("Please fill in all required fields");
      return;
    }

    // Get broker ID
    const broker = brokers?.find((b) => b.abbreviation === formData.broker);
    if (!broker?.brokerId) {
      alert("Invalid broker selected");
      return;
    }

    // Get action and position based on mappings
    const getActionAndPosition = (action) => {
      switch (action) {
        case "Buy":
          return { action: 0, position: 0 };
        case "Sell":
          return { action: 3, position: 0 };
        case "Short":
          return { action: 3, position: 1 };
        case "Cover":
          return { action: 0, position: 1 };
        default:
          return { action: 0, position: 0 };
      }
    };

    const { action: actionValue, position } = getActionAndPosition(
      formData.action
    );

    // Construct distribution key: account#fund_id#0#strategy_id
    let distributionKey = formData.account;

    // Find fund ID
    const selectedFund = funds?.find(
      (fund) => fund.abbreviation === formData.fund
    );
    if (selectedFund?.fundId) {
      distributionKey += `#${selectedFund.fundId}`;
    }

    // Add strategy if selected
    if (formData.strategy) {
      let key = findStrategyById(formData.strategy, sortedStrategies);
      distributionKey += `#0#${key}`;
    } else {
      distributionKey += `#0`;
    }

    // Create distribution object
    const distribution = {};
    distribution[distributionKey] = parseInt(formData.shareCount);

    // Get investment type enum value from asset type
    const investmentType = hashInvestmentReverse(
      formData.assetType.toLowerCase()
    );

    // If Options (assetType=10), validate option fields
    if (investmentType === 10) {
      const strike = Number(formData.strikePrice);
      const expMs = new Date(formData.expirationDate).getTime();
      if (
        (formData.option !== 0 && formData.option !== 1) ||
        !Number.isFinite(strike) ||
        strike <= 0 ||
        !formData.expirationDate ||
        !Number.isFinite(expMs)
      ) {
        alert(
          "Please fill in all required option fields (Options, Strike Price, Expiration Date)"
        );
        return;
      }
    }

    const optionFields =
      investmentType === 10
        ? {
            option: formData.option,
            strikePrice: Number(formData.strikePrice),
            expirationDate: new Date(formData.expirationDate).getTime(),
            underlying: {
              symbol: formData.ticker.toLowerCase(),
              currency: 7,
              exchange: 4,
              investment: 4,
            },
          }
        : {};

    // Create order data
    const orderData = {
      distribution,
      action: actionValue,
      position: position,
      broker: broker.brokerId,
      type: 1,
      typePrice: 0,
      instructions: "best way",
      routing: formData.routing === "Auto" ? 0 : 1,
      ticker: {
        symbol: formData.ticker.toLowerCase(),
        investment: investmentType !== null ? investmentType : 4, // Use selected asset type or default to Equity (4)
        exchange: 4,
        currency: 7,
        ...optionFields,
      },
      user: user,
    };

    // Create request data
    const requestData = {
      action: "modify_orders",
      data: [
        {
          order: orderData,
          tradeid: tradeData.tradeId,
        },
      ],
    };

    // Show loader and add to active requests
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Updating trade order..."));
    dispatch(addActiveRequests("modify_orders"));

    sendWebSocketMessage(JSON.stringify(requestData));
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 48,
              height: 48,
            }}
          >
            <EditIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              Edit Order
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Modify existing trading order
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2, overflowY: "visible" }}>
        <Grid container spacing={1.5}>
          {/* Fund Selection */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={sortedFunds}
              getOptionLabel={(option) => option.abbreviation || ""}
              value={
                sortedFunds.find(
                  (fund) => fund.abbreviation === formData.fund
                ) || null
              }
              onChange={(event, newValue) =>
                handleInputChange("fund", newValue?.abbreviation || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fund"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>

          {/* Strategy Selection (conditional) */}
          {sortedStrategies.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={sortedStrategies}
                getOptionLabel={(option) => option.name || option.strategyId}
                value={sortedStrategies.find(
                  (strategy) => strategy.name === formData.strategy
                )}
                onChange={(event, newValue) =>
                  handleInputChange("strategy", newValue?.name || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Strategy"
                    variant="outlined"
                    size="small"
                  />
                )}
                disableClearable
              />
            </Grid>
          )}
          {/* Asset Type */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={investArr}
              value={formData.assetType}
              onChange={(event, newValue) =>
                handleInputChange("assetType", newValue || "Equity")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset Type"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>
          {/* Action */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={actionOptions}
              getOptionLabel={(option) => option.label}
              value={
                actionOptions.find(
                  (action) => action.value === formData.action
                ) || null
              }
              onChange={(event, newValue) =>
                handleInputChange("action", newValue?.value || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Action"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>

          {/* Ticker */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={uniqueTickers}
              getOptionLabel={(option) => option.symbol}
              value={
                uniqueTickers.find(
                  (ticker) => ticker.symbol === formData.ticker
                ) || null
              }
              onChange={(event, newValue) => {
                handleInputChange("ticker", newValue?.symbol || "");
              }}
              freeSolo
              inputValue={formData.ticker}
              onInputChange={(event, newInputValue) => {
                handleInputChange("ticker", newInputValue.toUpperCase());
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ticker"
                  variant="outlined"
                  size="small"
                  placeholder="e.g., AAPL, MSFT"
                  required
                />
              )}
            />
          </Grid>

          {/* Options Fields (only when assetType=10) */}
          {showOptionFields && (
            <>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={[
                    { label: "Puts", value: 1 },
                    { label: "Calls", value: 0 },
                  ]}
                  getOptionLabel={(option) => option.label}
                  value={
                    formData.option === 0
                      ? { label: "Calls", value: 0 }
                      : { label: "Puts", value: 1 }
                  }
                  onChange={(event, newValue) =>
                    handleInputChange("option", newValue?.value ?? 1)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Options"
                      variant="outlined"
                      size="small"
                      required
                    />
                  )}
                  disableClearable
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Strike Price"
                  type="number"
                  value={formData.strikePrice}
                  onChange={(e) =>
                    handleInputChange("strikePrice", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Expiration Date"
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) =>
                    handleInputChange("expirationDate", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </>
          )}

          {/* Broker */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={sortedBrokers}
              getOptionLabel={(option) => option.abbreviation || ""}
              value={
                sortedBrokers.find(
                  (broker) => broker.abbreviation === formData.broker
                ) || null
              }
              onChange={(event, newValue) =>
                handleInputChange("broker", newValue?.abbreviation || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Broker"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>
          {/* Routing */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={["Manual", "Auto"]}
              value={
                formData.routing === 0 || formData.routing === "0"
                  ? "Auto"
                  : "Manual"
              }
              onChange={(event, newValue) =>
                handleInputChange("routing", newValue || "Manual")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Routing"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>

          {/* Account and Share Count Row */}
          <Grid size={{ xs: 6 }}>
            <Autocomplete
              options={sortedAccounts}
              value={formData.account || ""}
              onChange={(event, newValue) =>
                handleInputChange("account", newValue || "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Account"
                  variant="outlined"
                  size="small"
                  required
                />
              )}
              disableClearable
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="Share Count"
              value={formData.shareCount}
              onChange={(e) => handleInputChange("shareCount", e.target.value)}
              variant="outlined"
              size="small"
              type="number"
              required
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, justifyContent: "flex-end" }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            minWidth: 120,
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Update Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTradeDialog;
